// externals
import type { FC } from 'react';
import { useEffect, useRef, useState } from 'react';

// libraries
import { featureToggleSelectors } from '@makemydeal/dr-shared-store';

// types
import type { CellFinanceDetailsProps } from './types';

// constants/enums
import type { InterstateOnChangeCallback, InterstateOnChangeEvent } from '@interstate/components/InterstateEvents';
import { useFeesFormContext } from './useFeesFormContext';

// utils
import { encodeForDataTestId } from '../../utils/components';
import { useDispatch, useSelector } from 'react-redux';
import { formatNumberAndCents } from '@makemydeal/dr-common-utils';
import { NumericInput } from '@makemydeal/dr-shared-components/numeric-input';
import { convertToNumber } from '../../utils/formatUtils';
import * as offerActionCreators from '@makemydeal/dr-dash-store/actions/offerActionCreators';
import { dealLockHooks } from '@makemydeal/dr-dash-components';

export const FinanceAmountCell: FC<CellFinanceDetailsProps> = ({ detail, index }) => {
    const dispatch = useDispatch();

    // Local state for the numeric input value
    const [numericInputValue, setNumericInputValue] = useState(formatNumberAndCents(detail.amount));
    const prevAmountValue = useRef<number>(detail.amount ? convertToNumber(detail.amount) : 0);

    const isDealScreenRedDotErrorsEnabled = useSelector(featureToggleSelectors.isDealScreenRedDotErrorsEnabled);

    const isLocked = dealLockHooks.useIsDealLocked();
    const { handleInputChange, setDisableFeeSubmit, commitFeeSoftSave, handleSoftSaveOnEnterKey, handleInputBlur } =
        useFeesFormContext();

    const handleNumericInputChange: InterstateOnChangeCallback<string> = (e) => {
        const value = e.target.value;
        setNumericInputValue(value);
        handleFeeAmountChange(e);
    };

    const validateAndCommitChanges = () => {
        const newValue = convertToNumber(numericInputValue);
        if (prevAmountValue.current !== newValue) {
            prevAmountValue.current = newValue;
            commitFeeSoftSave();
        }
    };

    const handleBlur = (e: InterstateOnChangeEvent<string>) => {
        if (isDealScreenRedDotErrorsEnabled) {
            handleInputBlur({ category: detail.category, field: 'feeAmount', index, isValid: e.isValid });
        }

        if (numericInputValue === '') {
            handleFeeAmountChange({ target: { value: '' } });
            return;
        }

        const formattedValue = formatNumberAndCents(numericInputValue.replace(/,/g, ''));
        setTimeout(() => {
            setNumericInputValue(formattedValue);
            validateAndCommitChanges();
        });
    };

    const handleFeeAmountChange: InterstateOnChangeCallback<string> = (e) => {
        const value = e.target.value;
        const hasMoreThanTwoPoints = value.split('.').length > 2;

        if (hasMoreThanTwoPoints) {
            return;
        }

        setDisableFeeSubmit(value === '');

        dispatch(offerActionCreators.updatePaymentCallPendingState(prevAmountValue.current !== convertToNumber(value)));

        return handleInputChange({ category: detail.category, field: 'feeAmount', index, value });
    };

    useEffect(() => {
        if (detail.amount !== numericInputValue) {
            const formattedValue = formatNumberAndCents(detail.amount);
            setNumericInputValue(formattedValue);
        }
    }, [detail.amount]);

    return (
        <NumericInput
            displayLabel={false}
            label="Amount"
            name="feeAmount"
            onChange={handleNumericInputChange}
            value={numericInputValue}
            onBlur={(e) => handleBlur(e)}
            onKeyDown={handleSoftSaveOnEnterKey(validateAndCommitChanges)}
            data-testid={`edit-fee--amount-cell-${encodeForDataTestId(detail.name)}`}
            allowDecimal
            decimalMaxLength={2}
            decimalMinLength={0}
            inputPrefix={'$'}
            autoInsertCommas
            className="edit-fee--amount-cell"
            disabled={isLocked || detail.isDeleted}
            size="sm"
            required
            selectOnFocusSelector={featureToggleSelectors.isAutoSelectContentOnSlideOutsEnabledAfterBootstrap}
        />
    );
};
